import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Heart, ShoppingCart, Star, ChevronLeft, ChevronRight, Truck, ShieldCheck, RotateCcw, Minus, Plus, Zap } from "lucide-react";
import api from "@/lib/api";
import { formatINR } from "@/constants/branding";
import { useStore } from "@/context/StoreContext";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/ProductCard";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, isWished } = useStore();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    setQty(1);
    setActiveImg(0);
    async function load() {
      try {
        const { data } = await api.get(`/products/${id}`);
        if (!active) return;
        setProduct(data);
        const rel = await api.get(`/products?category=${data.category_slug}&limit=4`);
        if (active) setRelated(rel.data.items.filter((p) => p.id !== data.id).slice(0, 4));
      } catch {
        if (active) setNotFound(true);
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, [id]);

  if (loading) return <div className="text-center py-24 text-muted-foreground">Loading…</div>;
  if (notFound || !product)
    return (
      <div className="text-center py-24">
        <p className="font-semibold text-lg">This product is no longer available.</p>
        <Button className="mt-4" onClick={() => navigate("/products")}>Continue Shopping</Button>
      </div>
    );

  const off = product.mrp && product.mrp > product.price ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : 0;
  const wished = isWished(product.id);
  const outOfStock = product.stock <= 0;

  const buyNow = async () => {
    const ok = await addToCart(product.id, qty);
    if (ok) navigate("/cart");
  };

  const images = (product.images || []).filter(Boolean);

  return (
    <div className="shell py-5 sm:py-7 animate-fade-up">
      <nav className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground pb-4">
        <Link to="/" className="hover:text-primary">Home</Link>
        <ChevronRight className="h-3 w-3" />
        <Link to={`/products?category=${product.category_slug}`} className="hover:text-primary">{product.category}</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-foreground truncate max-w-[300px]">{product.name}</span>
      </nav>
      <button onClick={() => navigate(-1)} className="sm:hidden flex items-center gap-1 text-sm text-muted-foreground pb-3" data-testid="product-back">
        <ChevronLeft className="h-4 w-4" /> Back
      </button>

      <div className="surface overflow-hidden grid md:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        {/* Gallery + CTA */}
        <div className="p-4 sm:p-7 md:sticky md:top-[130px] md:self-start">
          <div className="flex gap-3">
            {images.length > 1 && (
              <div className="hidden sm:flex flex-col gap-2 shrink-0">
                {images.slice(0, 6).map((src, n) => (
                  <button
                    key={src + n}
                    onMouseEnter={() => setActiveImg(n)}
                    onClick={() => setActiveImg(n)}
                    className={`h-16 w-16 border-2 rounded-xl overflow-hidden bg-secondary ${n === activeImg ? "border-primary" : "border-border"}`}
                    aria-label={`Image ${n + 1}`}
                  >
                    <img src={src} alt="" className="h-full w-full object-contain p-1 mix-blend-multiply" />
                  </button>
                ))}
              </div>
            )}
            <div className="relative flex-1 aspect-square bg-secondary border border-border rounded-2xl overflow-hidden">
              <img src={images[activeImg] || images[0]} alt={product.name} className="absolute inset-0 h-full w-full object-contain p-5 mix-blend-multiply" />
              <button
                onClick={() => toggleWishlist(product.id)}
                className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white shadow-md flex items-center justify-center"
                data-testid="product-detail-wishlist"
                aria-label="Toggle wishlist"
              >
                <Heart className={`h-5 w-5 ${wished ? "fill-rose-500 text-rose-500" : "text-deep/60"}`} />
              </button>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2">
            <button
              disabled={outOfStock}
              onClick={() => addToCart(product.id, qty)}
              className="h-12 sm:h-14 rounded-xl border-2 border-primary text-primary hover:bg-primary hover:text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
              data-testid="product-detail-add-to-cart"
            >
              <ShoppingCart className="h-5 w-5" /> Add to Cart
            </button>
            <button
              disabled={outOfStock}
              onClick={buyNow}
              className="h-12 sm:h-14 rounded-xl bg-primary hover:bg-deep text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
              data-testid="product-detail-buy-now"
            >
              <Zap className="h-5 w-5 fill-white" /> Buy Now
            </button>
          </div>
        </div>

        {/* Info */}
        <div className="p-5 sm:p-8 md:border-l border-border">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Sold by {product.vendor_name}</p>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-deep mt-2 leading-tight" data-testid="product-detail-name">{product.name}</h1>

          {product.rating ? (
            <div className="mt-2 flex items-center gap-2">
              <span className={`rating-chip text-xs ${Number(product.rating) < 3 ? "mid" : ""}`}>
                <Star className="h-3 w-3 fill-current" /> {product.rating}
              </span>
            </div>
          ) : null}

          <p className="mt-4 text-xs font-bold uppercase tracking-widest text-success">Special price</p>
          <div className="flex items-baseline flex-wrap gap-x-3">
            <span className="text-4xl font-extrabold text-deep" data-testid="product-detail-price">{formatINR(product.price)}</span>
            {off > 0 && <span className="text-base text-muted-foreground line-through">{formatINR(product.mrp)}</span>}
            {off > 0 && <span className="text-xs font-extrabold bg-gold text-deep px-2.5 py-1 rounded-full">{off}% OFF</span>}
          </div>

          <p className="mt-2 text-sm">
            {outOfStock ? (
              <span className="text-destructive font-medium">Out of stock</span>
            ) : product.stock <= 5 ? (
              <span className="text-warning font-medium">Hurry, only {product.stock} left!</span>
            ) : (
              <span className="text-success font-medium">In stock</span>
            )}
          </p>

          {!outOfStock && (
            <div className="mt-5 flex items-center gap-4">
              <span className="text-sm font-medium text-muted-foreground w-24">Quantity</span>
              <div className="flex items-center gap-2">
                <button className="h-9 w-9 rounded-full border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-colors" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-decrease" aria-label="Decrease"><Minus className="h-4 w-4" /></button>
                <span className="w-12 text-center text-sm font-bold border border-border rounded-lg py-1.5" data-testid="qty-value">{qty}</span>
                <button className="h-9 w-9 rounded-full border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-colors" onClick={() => setQty((q) => Math.min(product.stock, q + 1))} data-testid="qty-increase" aria-label="Increase"><Plus className="h-4 w-4" /></button>
              </div>
            </div>
          )}

          <div className="mt-5 flex items-start gap-4 text-sm">
            <span className="font-medium text-muted-foreground w-24 shrink-0">Delivery</span>
            <div className="space-y-1.5">
              <p className="flex items-center gap-2"><Truck className="h-4 w-4 text-primary" /> Free delivery</p>
              <p className="flex items-center gap-2"><RotateCcw className="h-4 w-4 text-primary" /> Easy returns as per our return policy</p>
              <p className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Secure payment</p>
            </div>
          </div>

          <div className="mt-5 flex items-start gap-4 text-sm">
            <span className="font-medium text-muted-foreground w-24 shrink-0">Description</span>
            <p className="leading-relaxed whitespace-pre-line">{product.description}</p>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-6 surface">
          <h2 className="font-display text-2xl font-bold text-deep px-5 sm:px-7 pt-5 sm:pt-6">You may also like</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5 p-4 sm:p-7">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
